import React, { useState, useEffect, useRef, useCallback } from 'react';
import Box from '@elementor/ui/Box';
import Button from '@elementor/ui/Button';
import CircularProgress from '@elementor/ui/CircularProgress';
import IconButton from '@elementor/ui/IconButton';
import Typography from '@elementor/ui/Typography';
import { SearchIcon, XIcon, AIIcon } from '@elementor/icons';
import { LIMITS } from '../config';

/**
 * Reusable search input component with submit functionality
 * Using Elementor UI components
 */
function SearchInput({ onSubmit, placeholder, isLoading, initialValue, showSubmitButton, buttonText }) {
  const [query, setQuery] = useState(initialValue || '');

  useEffect(() => {
    if (initialValue !== undefined) {
      setQuery(initialValue);
    }
  }, [initialValue]);

  const trimmed = query.trim();
  const overLimit = trimmed.length > LIMITS.maxQuestionLength;
  const counterVisible = query.length > 400;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (trimmed && !isLoading && !overLimit) {
      onSubmit(trimmed);
    }
  };

  const textareaRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const maxHeight = 150;

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const singleLineHeight = 36;
    const newHeight = Math.min(el.scrollHeight, maxHeight);
    setIsExpanded(el.scrollHeight > singleLineHeight);
    el.style.height = newHeight + 'px';
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  useEffect(() => {
    autoResize();
  }, [query, autoResize]);

  const handleClear = () => {
    setQuery('');
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        width: '100%',
      }}
    >
      <Box
        className="ft-search-container"
        sx={{
          width: '100%',
          borderRadius: 1,
          border: '1px solid',
          borderColor: overLimit ? 'error.main' : 'divider',
          backgroundColor: 'background.paper',
          display: 'flex',
          flexDirection: (showSubmitButton && isExpanded) ? 'column' : { xs: showSubmitButton ? 'column' : 'row', sm: 'row' },
          alignItems: (showSubmitButton && isExpanded) ? 'stretch' : { xs: showSubmitButton ? 'stretch' : 'center', sm: 'center' },
          gap: (showSubmitButton && isExpanded) ? 1 : { xs: showSubmitButton ? 2 : 0, sm: 0 },
          p: 1,
          boxSizing: 'border-box',
          transition: 'border-color 0.2s',
          '&:hover': {
            borderColor: overLimit ? 'error.main' : 'action.disabled',
          },
          '&:focus-within': {
            borderColor: overLimit ? 'error.main' : 'primary.main',
            borderWidth: 2,
            p: 0.875,
          },
        }}
      >
        {/* Input row */}
        <Box className="ft-search-input-row" sx={{ display: 'flex', alignItems: 'flex-start', flex: 1, px: 1 }}>
          <SearchIcon className="ft-search-icon" sx={{ color: 'text.secondary', mr: 1.5, mt: '10px', flexShrink: 0 }} />
          <textarea
            ref={textareaRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
            placeholder={placeholder || "Search for whatever you want to learn..."}
            disabled={isLoading}
            autoFocus
            rows={1}
            maxLength={LIMITS.maxQuestionLength}
            aria-invalid={overLimit ? 'true' : 'false'}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              borderRadius: 0,
              backgroundColor: 'transparent',
              boxShadow: 'none',
              padding: '8px 0',
              margin: 0,
              fontSize: 14,
              color: 'inherit',
              width: '100%',
              fontFamily: 'inherit',
              transition: 'none',
              appearance: 'none',
              WebkitAppearance: 'none',
              resize: 'none',
              overflow: 'hidden',
              lineHeight: '1.4',
            }}
          />
          {isLoading && (
            <CircularProgress size={20} sx={{ color: 'text.secondary', ml: 1.5, mt: '10px' }} />
          )}
          {!isLoading && query && (
            <IconButton
              onClick={handleClear}
              size="small"
              aria-label="Clear search"
              sx={{ color: 'text.secondary', ml: 1, mt: '4px', p: 0.5 }}
            >
              <XIcon />
            </IconButton>
          )}
        </Box>

        {/* Submit button */}
        {showSubmitButton && !isLoading && (
          <Button
            className="ft-search-submit"
            type="submit"
            variant="contained"
            disabled={!trimmed || isLoading || overLimit}
            {...(!buttonText && { endIcon: <AIIcon /> })}
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: 16,
              px: 3,
              py: 1.5,
              whiteSpace: 'nowrap',
              backgroundColor: 'text.primary',
              color: 'background.paper',
              '&:hover': {
                backgroundColor: 'text.primary',
                opacity: 0.9,
              },
            }}
          >
            {buttonText || 'Get answer'}
          </Button>
        )}
      </Box>

      {counterVisible && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 0.5,
            px: 1,
          }}
        >
          <Typography
            variant="caption"
            color={overLimit ? 'error.main' : 'text.secondary'}
            aria-live="polite"
          >
            {query.length} / {LIMITS.maxQuestionLength}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default SearchInput;
